import { cn } from '../lib/cn';

interface SpinnerProps {
  /** Pixel diameter. */
  size?: number;
  className?: string;
  /** Accessible label for screen readers; hidden when decorative. */
  label?: string;
}

/**
 * A quiet rotating arc. The motion is paused under prefers-reduced-motion via
 * the global stylesheet, leaving a static ring that still communicates "busy".
 */
export function Spinner({ size = 20, className, label = 'Loading' }: SpinnerProps) {
  return (
    <span
      role="status"
      aria-label={label}
      className={cn('inline-flex', className)}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill="none"
        aria-hidden="true"
        style={{ animation: 'spin 0.8s linear infinite' }}
      >
        <circle cx="12" cy="12" r="9" stroke="var(--color-edge-strong)" strokeWidth="2.5" />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="var(--color-accent)"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
      <span className="sr-only">{label}</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }
        @media (prefers-reduced-motion: reduce) { svg { animation: none !important; } }`}</style>
    </span>
  );
}

/** Centered full-region loading state. */
export function LoadingRegion({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-ink-faint">
      <Spinner size={22} label={label} />
      <span className="text-sm">{label}…</span>
    </div>
  );
}
